import type { CastingInfo, CastProtocol } from "./foyerBridge";

/**
 * Guest-facing casting copy, derived from the normalized {@link CastingInfo}.
 *
 * The Screen-Share page must tell the guest exactly which button to look for on
 * their phone, and that button's NAME differs per receiver: a Philips MediaSuite
 * runs Google Cast ("Cast"), a Samsung panel speaks Miracast ("Smart View" /
 * "Wireless display"), an AirPlay receiver is "Screen Mirroring". Rather than
 * branching on the platform in the view, the view asks here - so every scheme
 * gets the same, correct wording for free.
 */

/** What the guest taps on their own device, per protocol. */
const PROTOCOL_ACTIONS: Record<CastProtocol, string> = {
  chromecast: "Cast",
  airplay: "AirPlay",
  miracast: "Smart View or Wireless display",
  // DIAL never surfaces as its own button - the guest presses the cast icon
  // inside YouTube/Netflix, which is the same gesture as Chromecast.
  dial: "Cast",
};

/**
 * The phrase naming the controls to look for, e.g. "Cast", "AirPlay or Cast".
 * Falls back to the generic pair when the device reports no receiver, which is
 * exactly the instructional fallback the page shows off a cast-capable TV.
 */
export function castActionLabel(info: CastingInfo | null | undefined): string {
  const seen: string[] = [];
  for (const p of info?.protocols ?? []) {
    const label = PROTOCOL_ACTIONS[p];
    if (label && !seen.includes(label)) seen.push(label);
  }
  if (!seen.length) return "AirPlay or Cast";
  if (seen.length === 1) return seen[0];
  return `${seen.slice(0, -1).join(", ")} or ${seen[seen.length - 1]}`;
}

/** A numbered instruction shown as one of the three Screen-Share tiles. */
export interface CastStep {
  /** "1" | "2" | "3" - the big numeral. */
  n: string;
  /** Short imperative title. */
  title: string;
  /** One-line detail. */
  detail: string;
}

export interface CastCopyArgs {
  /** The normalized casting model for this device (null off a real TV). */
  info: CastingInfo | null | undefined;
  /** What the guest picks in their cast list, e.g. "Room 214". */
  deviceName: string;
  /**
   * The network the guest must join first, when there is one. Present for a
   * hotspot transport (the TV's own SoftAP) and for the legacy MediaSuite flow
   * where the server mints credentials to display; absent on plain Wi-Fi Direct
   * and on a LAN receiver, where the guest joins nothing.
   */
  network?: { ssid: string; password?: string } | null;
}

/**
 * The three steps to show. Step 1 differs most: joining a hotspot, versus using
 * the house Wi-Fi, versus nothing to join at all on Wi-Fi Direct - where the
 * receiver advertises itself directly and telling the guest to "join a network"
 * would send them down a dead end.
 */
export function castSteps(args: CastCopyArgs): CastStep[] {
  const { info, deviceName, network } = args;
  const action = castActionLabel(info);
  const connect: CastStep = network
    ? {
        n: "1",
        title: "Connect",
        detail: `Join the Wi-Fi network “${network.ssid}”`,
      }
    : info?.transport === "wifi-direct"
      ? {
          n: "1",
          title: "Unlock",
          detail: "Unlock your phone and keep it near the television",
        }
      : {
          n: "1",
          title: "Connect",
          detail: "Join the same Wi-Fi network as this television",
        };
  return [
    connect,
    { n: "2", title: "Open", detail: `Open ${action} on your device` },
    {
      n: "3",
      title: "Share",
      detail: `Choose “${deviceName}” to start sharing`,
    },
  ];
}
